/**
 * Lazy loading for optional SDKs. Nothing here runs until a real provider is used,
 * so no SDK enters the first-load bundle.
 *
 * `optionalModule` imports a specifier held in a variable: bundlers leave it alone
 * (no resolution, no chunk), so the package need not be installed for web builds.
 * A native build that ships the plugin passes its own loader instead, e.g.
 * `load: () => import('@capacitor-community/admob')`, which Vite splits into a lazy chunk.
 */
export type Loader<T> = () => Promise<T>;

export function optionalModule<T>(specifier: string): Loader<T> {
  let pending: Promise<T> | null = null;
  return () => {
    pending ??= (import(/* @vite-ignore */ specifier) as Promise<T>).catch((e: unknown) => {
      pending = null; // allow a later retry (e.g. after going online)
      throw e;
    });
    return pending;
  };
}

const scripts = new Map<string, Promise<void>>();

/** Inject a third-party <script> once; later calls share the same promise. */
export function loadScript(src: string, doc: Document = document, attrs: Record<string, string> = {}): Promise<void> {
  let loading = scripts.get(src);
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const el = doc.createElement('script');
      el.src = src;
      el.async = true;
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      el.onload = () => resolve();
      el.onerror = () => {
        scripts.delete(src);
        el.remove();
        reject(new Error(`failed to load ${src}`));
      };
      doc.head.appendChild(el);
    });
    scripts.set(src, loading);
  }
  return loading;
}

/** Random id for idempotency keys and nonces. */
export function randomId(): string {
  return crypto.randomUUID();
}
